import React, { useState, useEffect } from "react";
import { useCreateSource, useUpdateSource } from "../../hooks/useApi";
import { useTranslation } from "../../hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import ValidationMessage from "../forms/ValidationMessage";
import { parseApiError } from "../../utils/validation";
import ImageUpload from "../ImageUpload";
import { ShoppingCart } from "lucide-react";

/**
 * Dialog to create OR edit a PRODUCT source: name, description, image (URL),
 * price, link (optional). Each product is its own source and renders as a
 * product CARD in chat. Only MANUAL products (product_origin === "manual") are
 * editable — products detected from a crawled URL are read-only.
 */
const ProductSourceDialog = ({
  isOpen,
  onOpenChange,
  botId,
  onSuccess,
  editSource = null,
}) => {
  const createSourceMutation = useCreateSource();
  const updateSourceMutation = useUpdateSource();
  const { t } = useTranslation();

  const isEditMode = Boolean(editSource);

  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [immagine, setImmagine] = useState("");
  const [prezzo, setPrezzo] = useState("");
  const [link, setLink] = useState("");
  const [formError, setFormError] = useState("");

  // Prefill when editing, clear when creating
  useEffect(() => {
    if (!isOpen) return;
    if (isEditMode) {
      setNome(editSource.product_name || "");
      setDescrizione(
        editSource.product_description ||
          editSource.product_metadata?.description ||
          ""
      );
      setImmagine(editSource.product_image || "");
      setPrezzo(editSource.product_price || "");
      setLink(editSource.product_url || "");
    } else {
      setNome("");
      setDescrizione("");
      setImmagine("");
      setPrezzo("");
      setLink("");
    }
    setFormError("");
  }, [isOpen, isEditMode, editSource]);

  const isSubmitting = isEditMode
    ? updateSourceMutation.isPending
    : createSourceMutation.isPending;

  const isValidUrl = (v) => /^https?:\/\/.+/.test((v || "").trim());
  // Link is optional; only require the name (and a valid URL IF one is entered).
  const canSubmit = nome.trim() && (!link.trim() || isValidUrl(link));

  const handleOpenChange = (open) => {
    if (!open) {
      setNome("");
      setDescrizione("");
      setImmagine("");
      setPrezzo("");
      setLink("");
      setFormError("");
    }
    onOpenChange(open);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const n = nome.trim();
    const l = link.trim();
    if (!n) {
      setFormError(
        t("dialogs.product.errorName", "Il nome del prodotto è obbligatorio.")
      );
      return;
    }
    if (l && !isValidUrl(l)) {
      setFormError(
        t(
          "dialogs.product.errorLink",
          "Il link deve essere un URL valido (http/https)."
        )
      );
      return;
    }
    const fields = {
      product_name: n,
      product_description: descrizione.trim() || null,
      product_image: immagine.trim() || null,
      product_price: prezzo.trim() || null,
      product_url: l || null,
    };
    try {
      if (isEditMode) {
        await updateSourceMutation.mutateAsync({
          botId,
          sourceId: editSource._id,
          data: fields,
        });
      } else {
        await createSourceMutation.mutateAsync({
          botId,
          data: { type: "product", ...fields },
        });
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error(
        isEditMode
          ? "Failed to update product source:"
          : "Failed to create product source:",
        error
      );
      setFormError(parseApiError(error).message);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            {isEditMode
              ? t("dialogs.product.titleEdit", "Modifica prodotto")
              : t("dialogs.product.titleAdd", "Aggiungi prodotto")}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? t(
                  "dialogs.product.descEdit",
                  "Aggiorna i dati del prodotto. La modifica verrà re-indicizzata nella knowledge base."
                )
              : t(
                  "dialogs.product.desc",
                  "Un prodotto come fonte a sé: viene mostrato come scheda prodotto nella chat."
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="product-name" className="block text-sm font-medium">
              {t("dialogs.product.nameLabel", "Nome")}
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="product-name"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={t(
                "dialogs.product.namePlaceholder",
                "Es. Anker Solix Solarbank 2"
              )}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="product-description"
              className="block text-sm font-medium"
            >
              {t("dialogs.product.descriptionLabel", "Descrizione")}
            </Label>
            <Textarea
              id="product-description"
              rows={4}
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder={t(
                "dialogs.product.descriptionPlaceholder",
                "Descrizione del prodotto (indicizzata per la ricerca)"
              )}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-price" className="block text-sm font-medium">
              {t("dialogs.product.priceLabel", "Prezzo")}
            </Label>
            <Input
              id="product-price"
              value={prezzo}
              onChange={(e) => setPrezzo(e.target.value)}
              placeholder={t("dialogs.product.pricePlaceholder", "Es. € 199,00")}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label className="block text-sm font-medium">
              {t("dialogs.product.imageLabel", "Immagine")}
            </Label>
            <ImageUpload
              value={immagine}
              botId={botId}
              onChange={setImmagine}
              alt={nome || "prodotto"}
              label={t("dialogs.product.imageUploaded", "Immagine caricata")}
              placeholder="https://.../foto.jpg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="product-link" className="block text-sm font-medium">
              {t("dialogs.product.linkLabel", "Link")}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                ({t("common.optional", "opzionale")})
              </span>
            </Label>
            <Input
              id="product-link"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://..."
              disabled={isSubmitting}
            />
          </div>

          {formError && (
            <ValidationMessage
              type="error"
              message={formError}
              showIcon={true}
              size="md"
            />
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel", "Annulla")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !canSubmit}>
              {isEditMode
                ? isSubmitting
                  ? t("dialogs.product.saving", "Salvataggio...")
                  : t("dialogs.product.saveChanges", "Salva modifiche")
                : isSubmitting
                ? t("dialogs.product.creating", "Creazione...")
                : t("dialogs.product.addSource", "Aggiungi prodotto")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductSourceDialog;
